/**
 * useSessionRecovery Hook - Logica de Recuperacao de Sessao
 * @module features/chat/hooks/useSessionRecovery
 *
 * Hook para detectar e gerenciar recuperacao de sessoes anteriores.
 * Verifica TTL de 24h e exibe toasts informativos ao usuario.
 *
 * T005-FE: Logica de Recuperacao de Sessao
 *
 * Features:
 * - Detecta sessao anterior no LocalStorage
 * - Verifica TTL de 24h usando store.isSessionExpired
 * - Exibe toast "Sessao recuperada" ou "Sessao expirada"
 * - Integracao com sonner (toast library)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { getHasHydrated, getWasRestoredFromStorage, useSessionStore } from '../stores/sessionStore';

// =============================================================================
// TYPES
// =============================================================================

export interface UseSessionRecoveryReturn {
  /**
   * Indica se a verificacao inicial esta em andamento
   */
  isLoading: boolean;

  /**
   * Indica se uma sessao anterior foi recuperada com sucesso
   */
  isRecovered: boolean;

  /**
   * Indica se a sessao anterior estava expirada (TTL > 24h)
   */
  isExpired: boolean;

  /**
   * Funcao para disparar verificacao manual da sessao
   */
  checkSession: () => void;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Tempo em ms para considerar uma sessao como "nova"
 * Sessoes criadas ha menos de 5 segundos nao sao consideradas "recuperadas"
 */
const NEW_SESSION_THRESHOLD_MS = 5000;

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Verifica se existe dados significativos na sessao
 * Uma sessao e considerada "com dados" se tiver nome ou email preenchido
 */
function hasSessionData(userData: {
  name: string | undefined;
  email: string | undefined;
}): boolean {
  const hasName = userData.name !== undefined && userData.name.trim() !== '';
  const hasEmail = userData.email !== undefined && userData.email.trim() !== '';
  return hasName || hasEmail;
}

/**
 * Verifica se a sessao foi criada recentemente (sessao nova)
 * Retorna true se a sessao foi criada ha menos de NEW_SESSION_THRESHOLD_MS
 */
function isNewSession(startedAt: Date | string): boolean {
  try {
    const sessionStart = new Date(startedAt).getTime();
    const now = Date.now();
    return now - sessionStart < NEW_SESSION_THRESHOLD_MS;
  } catch {
    return false;
  }
}

// FIX-006: Funcao hasStoredSession removida - substituida por _wasRestoredFromStorage flag
// que e mais confiavel pois verifica durante hydration se havia dados REAIS

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook para gerenciar recuperacao de sessao
 *
 * Executa automaticamente na montagem do componente e verifica:
 * 1. Se existe sessao salva no localStorage
 * 2. Se a sessao nao expirou (TTL de 24h)
 * 3. Se a sessao tem dados significativos
 *
 * @returns Estado da recuperacao e funcao para re-verificar
 *
 * @example
 * ```tsx
 * function ChatPage() {
 *   const { isLoading, isRecovered, isExpired } = useSessionRecovery();
 *
 *   if (isLoading) {
 *     return <Loading />;
 *   }
 *
 *   if (isRecovered) {
 *     // Sessao recuperada, continuar de onde parou
 *   }
 *
 *   return <Chat />;
 * }
 * ```
 */
export function useSessionRecovery(): UseSessionRecoveryReturn {
  const [isLoading, setIsLoading] = useState(true);
  const [isRecovered, setIsRecovered] = useState(false);
  const [isExpired, setIsExpired] = useState(false);

  // Ref para evitar execucao multipla
  const hasChecked = useRef(false);

  // Store hooks
  const session = useSessionStore((state) => state.session);
  const clearSession = useSessionStore((state) => state.clearSession);
  const isSessionExpired = useSessionStore((state) => state.isSessionExpired);
  const hasHydrated = useSessionStore(getHasHydrated); // FIX-005
  const wasRestoredFromStorage = useSessionStore(getWasRestoredFromStorage); // FIX-006

  /**
   * Verifica o estado da sessao e exibe toast apropriado
   */
  const checkSession = useCallback(() => {
    setIsLoading(true);

    try {
      // FIX-006: Usar flag _wasRestoredFromStorage ao inves de hasStoredSession()
      // Isso evita falsos positivos quando o Zustand auto-salva uma sessao vazia
      if (!wasRestoredFromStorage) {
        // Sessao nova, sem dados anteriores (ou apenas estado inicial salvo pelo Zustand)
        setIsRecovered(false);
        setIsExpired(false);
        setIsLoading(false);
        return;
      }

      // Verifica se a sessao foi criada recentemente (sessao nova, nao recuperada)
      // Isso evita o popup quando o Zustand salva uma sessao nova no localStorage
      if (isNewSession(session.startedAt)) {
        setIsRecovered(false);
        setIsExpired(false);
        setIsLoading(false);
        return;
      }

      // Verifica se a sessao expirou (TTL > 24h)
      const expired = isSessionExpired();

      if (expired) {
        // Sessao expirada
        setIsExpired(true);
        setIsRecovered(false);

        toast.warning('Sessao expirada', {
          description: 'Sua sessao anterior expirou. Iniciando nova sessao.',
          duration: 4000,
        });

        // Limpa a sessao expirada
        clearSession();
        setIsLoading(false);
        return;
      }

      // Verifica se a sessao tem dados significativos (nome, email ou mensagens)
      const hasData = hasSessionData(session.userData);
      const hasMessages = session.messages && session.messages.length > 0;

      if (hasData || hasMessages) {
        // Sessao valida com dados recuperados
        setIsRecovered(true);
        setIsExpired(false);

        toast.success('Sessao recuperada', {
          description: 'Continuando de onde voce parou.',
          duration: 3000,
        });
      } else {
        // Sessao existe mas sem dados significativos
        setIsRecovered(false);
        setIsExpired(false);
      }
    } catch (error) {
      // Erro ao acessar localStorage ou processar dados
      console.error('Erro ao verificar sessao:', error);
      setIsRecovered(false);
      setIsExpired(false);
    } finally {
      setIsLoading(false);
    }
  }, [
    wasRestoredFromStorage,
    session.userData,
    session.startedAt,
    session.messages,
    clearSession,
    isSessionExpired,
  ]);

  /**
   * Executa verificacao automatica na montagem
   * Usa ref para evitar execucao multipla em re-renders
   * FIX-005: Aguarda hydration do Zustand antes de verificar
   */
  useEffect(() => {
    // FIX-005: Aguardar hydration completar antes de verificar sessao
    if (!hasHydrated) {
      return;
    }

    if (hasChecked.current) {
      return;
    }

    hasChecked.current = true;
    checkSession();
  }, [hasHydrated, checkSession]);

  return {
    isLoading,
    isRecovered,
    isExpired,
    checkSession,
  };
}

export default useSessionRecovery;
